import cx from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Translate } from 'next-translate';
import useTranslation from 'next-translate/useTranslation';
import React, {
  FunctionComponent,
  MouseEventHandler,
  useCallback,
  useMemo,
  useState,
} from 'react';

import useLinkProps, {
  originalRoutes,
} from '../../../../../hooks/use-link-props';
import CategoryHeader from '../../../categories/header';
import MenuLink from '../../../links/menu';
import PartNameWithStock from '../common/part-name-with-stock';

import generateLink from './generate-link';
import classes from './navigation.module.css';
import FeaturedLink from '../../../links/featured';
import CollapsibleList from '../../../collapsible-list/menu';
import { selectVehicleStepAtom, vehiclePickerOpen } from '../../../../../state/vehicle';
import { useAtom } from 'jotai';
import BPModal from '../../../modal';
import PageTitleSEO from '../../typography/page-title';
import useLoggedUser from '../../../../../hooks/use-logged-user';
import AlertForm from '../../../../modules/v2/create-part-alert/form';
import { brandAtom, modelAtom, partAtom, referenceAtom, versionAtom } from '../../../../../hooks/use-picker-selection';

export const ListSeparator = ({
  id,
  inColumns,
  t,
  locale,
}: {
  id: number;
  inColumns: boolean;
  t: Translate;
  locale?: string;
}) => {
  const groupName = useMemo(() => {
    switch (id) {
      case 1:
        return t('header:groups.group-1');
      case 2:
        return t('header:groups.group-2');
      case 3:
        return t('header:groups.group-3');
      default:
        return;
    }
  }, [id, t]);

  return (
    <li
      //role="separator"
      className={cx(
        classes.columnSpan,
        { [classes.splitInColumns]: inColumns },
        'text-2xl',
        'pl-4',
        'text-blue-100'
      )}
    >
      {groupName}
    </li>
  );
};

export type CategoryListProps = {
  groups: Array<TCategory>;
  onItemClick?: MouseEventHandler;
  replaceNavigationFn?: Function;
  slug?: string;
  linkToNavigate?: keyof typeof originalRoutes;
  /**
   * Make items to appear in a 4 columns (only desktop)
   */
  splitInColumns?: boolean;
  vehicle?: any;
  isPartsMenu?: boolean;
};

const DefaultList: FunctionComponent<CategoryListProps> = ({
  groups,
  splitInColumns,
  onItemClick,
  replaceNavigationFn,
  slug,
  vehicle,
  isPartsMenu = false,
  linkToNavigate,
}) => {
  const { locale, query } = useRouter();
  const { t } = useTranslation();
  const getLink = useLinkProps();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedInfo, setSelectedInfo] = useState({});
  const user = useLoggedUser();
  const someOnlyProAvailable = groups.filter(c => c.someNotAllowed).length > 0;

  //Atoms for create alert
  const [, setVehiclePicker] = useAtom(vehiclePickerOpen);
  const [, setSelectedVehicleStep] = useAtom(selectVehicleStepAtom);
  const [, setPart] = useAtom(partAtom);
  const [, setReference] = useAtom(referenceAtom);
  const [, setBrandAtom] = useAtom(brandAtom);
  const [, setModelAtom] = useAtom(modelAtom);
  const [, setVersionAtom] = useAtom(versionAtom);

  const openVehiclePicker = (part, brand, model, version, reference) => {
    setPart(part ? {
      value: part?.slug,
      label: part?.name,
      is_pro_allowed: part?.is_pro_allowed,
      is_allowed: part?.is_allowed,
    } : null);

    setReference(reference || null);

    setBrandAtom(brand ? {
      ...(brand),
      value: brand?.slug,
      label: brand?.name,
    } : null);

    setModelAtom(model ? {
      ...model,
      value: model?.slug,
      label: model?.name,
    } : null);

    setVersionAtom(version ? {
      ...version,
      value: version?.slug,
      label: version?.name,
    } : null);

    if (user) {
      setSelectedVehicleStep('CREATE_PART_ALERT');
    } else {
      setSelectedVehicleStep('CREATE_PART_ALERT_LOGIN');
    }

    setVehiclePicker((prevValue) => !prevValue);
  };

  return (
    <>
      <CollapsibleList
        items={groups?.map(
          (
            {
              id,
              name,
              logo_small,
              logo_big,
              parts,
              slug: categorySlug,
              total_products,
              isNotAllowedFullCategory,
              someNotAllowed
            },
            index
          ) => ({
            header: (
              <CategoryHeader
                index={index}
                id={id}
                name={name}
                stock={total_products}
                parts={parts.length}
                isSideMenu={isPartsMenu}
                isNotAllowedFullCategory={isNotAllowedFullCategory}
                someNotAllowed={someNotAllowed}
              />
            ),
            content: (
              <>
                <ul className={cx({ [classes.list]: splitInColumns })}>
                  {Object.entries(parts).map(([group_id, g]) => (
                    <React.Fragment key={`${categorySlug}-${group_id}`}>
                      {parseInt(group_id, 10) > 0 && (
                        <ListSeparator
                          id={parseInt(group_id, 10)}
                          inColumns={splitInColumns}
                          t={t}
                          locale={locale}
                        />
                      )}
                      {g.map(
                        ({
                          id,
                          name,
                          slug: partSlug,
                          total_products: totalPartProducts,
                          is_pro_allowed: isAllowedOnlyOnPro,
                          is_allowed: isAllowed,
                        }: TPart) => {
                          const linkDataToNavigate = linkToNavigate
                            ? getLink(linkToNavigate, {
                              category: categorySlug,
                              subCategory: partSlug,
                            })
                            : generateLink({
                              getLink,
                              slug,
                              partSlug,
                              categorySlug,
                            });
                          return (
                            <li key={id} className="w-full inline-block">
                              {!replaceNavigationFn ? (
                                <>{
                                  totalPartProducts <= 0 ? <MenuLink
                                    //disabled={totalPartProducts <= 0}
                                    onClick={() => {
                                      if (totalPartProducts <= 0) {
                                        openVehiclePicker(
                                          {
                                            id,
                                            name,
                                            slug: partSlug,
                                            total_products: totalPartProducts,
                                            is_pro_allowed: isAllowedOnlyOnPro,
                                            is_allowed: isAllowed,
                                          },
                                          vehicle.brand,
                                          vehicle.model,
                                          vehicle.version,
                                          null
                                        )
                                        return
                                      };
                                    }}
                                  >
                                    {
                                      <PartNameWithStock
                                        name={name}
                                        slug={partSlug}
                                        display={
                                          splitInColumns ? 'inline' : 'column'
                                        }
                                        nameTextTag={'h4'}
                                        totalStock={totalPartProducts}
                                        isAllowedOnlyOnPro={isAllowedOnlyOnPro}
                                        isAllowed={isAllowed}
                                      />
                                    }
                                  </MenuLink> :
                                    <Link
                                      {...linkDataToNavigate}
                                      passHref
                                      shallow
                                      prefetch={false}
                                      legacyBehavior
                                      className="pointer-events-none"
                                      itemProp="category"
                                    >
                                      <MenuLink
                                        //disabled={totalPartProducts <= 0}
                                        onClick={() => {
                                          onItemClick &&
                                            onItemClick({
                                              slug: partSlug,
                                              subgroup_slug: categorySlug,
                                            })
                                        }}
                                      >
                                        {
                                          <PartNameWithStock
                                            name={name}
                                            slug={partSlug}
                                            display={
                                              splitInColumns ? 'inline' : 'column'
                                            }
                                            nameTextTag={'h4'}
                                            totalStock={totalPartProducts}
                                            isAllowedOnlyOnPro={isAllowedOnlyOnPro}
                                            isAllowed={isAllowed}
                                          />
                                        }
                                      </MenuLink>
                                    </Link>
                                }
                                </>
                              ) : (
                                <MenuLink
                                  //disabled={totalPartProducts <= 0}
                                  onClick={() => {
                                    if (totalPartProducts <= 0) {
                                      if (isAllowed) {
                                        openVehiclePicker(
                                          {
                                            id,
                                            name,
                                            slug: partSlug,
                                            total_products: totalPartProducts,
                                            is_pro_allowed: isAllowedOnlyOnPro,
                                            is_allowed: isAllowed,
                                          },
                                          vehicle.brand,
                                          vehicle.model,
                                          vehicle.version,
                                          null
                                        )
                                        return
                                      }
                                      return
                                    };
                                    replaceNavigationFn({
                                      name,
                                      id,
                                      slug: partSlug,
                                      subgroup_slug: categorySlug,
                                    })
                                  }}
                                >
                                  <PartNameWithStock
                                    name={name}
                                    slug={partSlug}
                                    display={splitInColumns ? 'inline' : 'column'}
                                    nameTextTag={'h4'}
                                    totalStock={totalPartProducts}
                                    isAllowedOnlyOnPro={isAllowedOnlyOnPro}
                                    isAllowed={isAllowed}
                                  />
                                </MenuLink>
                              )}
                            </li>
                          );
                        }
                      )}
                    </React.Fragment>
                  ))}
                </ul>
                <ul
                  className={cx('flex flex-row items-end pl-4 w-full pb-6 text-right',
                    'justify-end ', {
                    hidden: isPartsMenu,
                  })}
                >
                  <div className="flex max-lg:text-center justify-end text-base lg:text-xl">
                    <Link
                      {...getLink(
                        query.category
                          ? 'sub-categories-with-vehicle'
                          : 'sub-categories',
                        {
                          category: query.category
                            ? query.category
                            : categorySlug,
                          slug: query.category ? categorySlug : '',
                        }
                      )}
                      legacyBehavior
                      passHref
                      prefetch={false}
                      shallow
                    >
                      <FeaturedLink
                        variant="blackBold"
                        className="text-[20px] max-lg:text-base"
                      >
                        {t(`product:see_more`)}
                      </FeaturedLink>
                    </Link>
                  </div>
                </ul>

              </>
            ),
            key: id,
            disabled: total_products <= 0,
          })
        )}
      />
    </>

  );
};

export default DefaultList;
